/**
 * GuidanceControls - Sliders to guide creature behavior
 */

import { useCallback } from 'react';
import { useSimulationStore } from '../stores/simulationStore';
import { Tooltip } from './ui';
import type { GuidanceSettings } from '../types/simulation';

interface GuidanceControlsProps {
  onGuidanceChange: (guidance: Partial<GuidanceSettings>) => void;
  onClearWaypoint: () => void;
}

interface SliderProps {
  label: string;
  tooltip?: string;
  value: number;
  onChange: (value: number) => void;
  leftLabel: string;
  rightLabel: string;
  disabled?: boolean;
}

function GuidanceSlider({ label, tooltip, value, onChange, leftLabel, rightLabel, disabled }: SliderProps) {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(parseFloat(e.target.value));
    },
    [onChange]
  );

  // Convert -0.5 to 0.5 range to 0 to 100 for display
  const displayValue = Math.round((value + 0.5) * 100);

  const labelElement = tooltip ? (
    <Tooltip content={tooltip}>
      <span className="slider-label">{label}</span>
    </Tooltip>
  ) : (
    <span className="slider-label">{label}</span>
  );

  return (
    <div className="guidance-slider">
      <div className="slider-header">
        {labelElement}
        <span className="slider-value">{value > 0 ? '+' : ''}{value.toFixed(2)}</span>
      </div>
      <div className="slider-container">
        <span className="slider-end-label left">{leftLabel}</span>
        <input
          type="range"
          min="-0.5"
          max="0.5"
          step="0.05"
          value={value}
          onChange={handleChange}
          disabled={disabled}
          className="slider-input"
        />
        <span className="slider-end-label right">{rightLabel}</span>
      </div>
      <div className="slider-track-labels">
        <div
          className="slider-indicator"
          style={{ left: `${displayValue}%` }}
        />
      </div>
    </div>
  );
}

export function GuidanceControls({ onGuidanceChange, onClearWaypoint }: GuidanceControlsProps) {
  const myCreatureAlive = useSimulationStore((state) => state.myCreatureAlive);
  const guidance = useSimulationStore((state) => state.guidance);
  const waypoint = useSimulationStore((state) => state.waypoint);

  const disabled = !myCreatureAlive;

  const handleThrustChange = useCallback(
    (value: number) => onGuidanceChange({ thrustBias: value }),
    [onGuidanceChange]
  );

  const handleAggressionChange = useCallback(
    (value: number) => onGuidanceChange({ aggression: value }),
    [onGuidanceChange]
  );

  const handleExplorationChange = useCallback(
    (value: number) => onGuidanceChange({ exploration: value }),
    [onGuidanceChange]
  );

  const handleReproductionChange = useCallback(
    (value: number) => onGuidanceChange({ reproduction: value }),
    [onGuidanceChange]
  );

  return (
    <div className="panel guidance-controls">
      <h3>Guia de Comportamiento</h3>

      {!myCreatureAlive ? (
        <p className="hint-text">Crea una criatura primero</p>
      ) : (
        <>
          <p className="guidance-hint">
            Ajusta las preferencias de tu criatura. El cerebro neural decide, pero estas preferencias influyen en sus decisiones.
          </p>

          <GuidanceSlider
            label="Velocidad"
            tooltip="Controla la preferencia de velocidad. Mas rapido consume mas energia."
            value={guidance.thrustBias}
            onChange={handleThrustChange}
            leftLabel="Lento"
            rightLabel="Rapido"
            disabled={disabled}
          />

          <GuidanceSlider
            label="Agresividad"
            tooltip="Define si la criatura huye o ataca a otras criaturas cercanas."
            value={guidance.aggression}
            onChange={handleAggressionChange}
            leftLabel="Huir"
            rightLabel="Atacar"
            disabled={disabled}
          />

          <GuidanceSlider
            label="Exploracion"
            tooltip="Tendencia a explorar nuevas areas vs permanecer en zona conocida."
            value={guidance.exploration}
            onChange={handleExplorationChange}
            leftLabel="Quedarse"
            rightLabel="Explorar"
            disabled={disabled}
          />

          <GuidanceSlider
            label="Reproduccion"
            tooltip="Prioridad para buscar pareja. Requiere energia suficiente para reproducirse."
            value={guidance.reproduction}
            onChange={handleReproductionChange}
            leftLabel="Evitar"
            rightLabel="Buscar"
            disabled={disabled}
          />

          {waypoint && (
            <div className="waypoint-info">
              <span>Waypoint activo: ({Math.round(waypoint.x)}, {Math.round(waypoint.y)})</span>
              <button className="clear-waypoint" onClick={onClearWaypoint}>
                Limpiar
              </button>
            </div>
          )}

          <p className="waypoint-hint">
            Haz click en el mundo para establecer un waypoint
          </p>
        </>
      )}
    </div>
  );
}
